import React, { useState, useEffect } from 'react';
import { Button, Modal } from '../../components';
import { Input, Typography } from 'antd';
import {
    useNetworkTroubleshootingMutation,
    useLazyGetNetworkTroubleshootingStatusQuery,
} from '../../api/networkTroubleshooting';
import { handleError } from '../../lib/helpers';
import { useNotificationContext } from '../../providers';
import { validateSourceIP, networkTroubleshootingUtilities } from './utils';

const { Title, Text } = Typography;

export const NetworkTroubleshootingModal = ({ open, onClose, applianceId, applianceName }) => {
    const { notifyError } = useNotificationContext();

    const [sourceIP, setSourceIP] = useState('');
    const [result, setResult] = useState('');
    const [isExecuting, setIsExecuting] = useState(false);
    const [currentUtility, setCurrentUtility] = useState('');
    const [troubleshootingId, setTroubleshootingId] = useState(null);
    const [triggerNetworkTroubleshooting] = useNetworkTroubleshootingMutation();
    const [getNetworkTroubleshootingStatus] = useLazyGetNetworkTroubleshootingStatusQuery();

    // Helper to reset execution state
    const stopExecution = () => {
        setIsExecuting(false);
        setTroubleshootingId(null);
    };

    // Manual polling
    useEffect(() => {
        let pollingTimer;
        if (isExecuting && troubleshootingId) {
            const poll = async () => {
                try {
                    const statusResponse = await getNetworkTroubleshootingStatus({ id: troubleshootingId });

                    if (statusResponse.error) {
                        stopExecution();
                        handleError(statusResponse.error, notifyError, `Failed to get ${currentUtility} results`);
                        return;
                    }

                    const statusData = statusResponse.data;
                    if (statusData && statusData.state === 'success') {
                        setResult(statusData.output || 'Command completed successfully');
                        stopExecution();
                    } else if (statusData && statusData.state === 'error') {
                        stopExecution();
                        handleError(
                            statusData,
                            notifyError,
                            `Failed to run ${currentUtility} command: ${statusData.error || 'Unknown error'}`,
                        );
                    } else {
                        // Continue polling if still in progress
                        pollingTimer = setTimeout(poll, 500);
                    }
                } catch (pollError) {
                    stopExecution();
                    handleError(pollError, notifyError, `Failed to get ${currentUtility} results`);
                }
            };

            // Start polling immediately
            poll();
        }
        return () => {
            if (pollingTimer) clearTimeout(pollingTimer);
        };
    }, [isExecuting, troubleshootingId, currentUtility, notifyError]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!open) {
            setSourceIP('');
            setResult('');
            setIsExecuting(false);
            setCurrentUtility('');
            setTroubleshootingId(null);
        }
    }, [open]);

    const handleUtilityClick = async (utility) => {
        if (!validateSourceIP(sourceIP)) {
            notifyError({ description: 'Please provide a valid Source IP address' });
            return;
        }
        setIsExecuting(true);
        setCurrentUtility(utility);
        setResult('');
        setTroubleshootingId(null);

        const callInfo = {
            cloud_account_id: applianceId,
            source_ip: sourceIP.trim(),
            utility: utility,
        };
        try {
            // Start the network troubleshooting command
            const response = await triggerNetworkTroubleshooting(callInfo).unwrap();
            const { id } = response;
            setTroubleshootingId(id);
        } catch (error) {
            stopExecution();
            handleError(error, notifyError, `Failed to run ${utility} command`);
        }
    };

    return (
        <Modal
            title={`Network Utilities: ${applianceName}`}
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>,
            ]}
            width={700}
            className="network-troubleshooting-modal"
        >
            <div className="source-input-section">
                <Text strong className="input-label">
                    Source IP or FQDN
                </Text>
                <Input
                    value={sourceIP}
                    onChange={(e) => setSourceIP(e.target.value)}
                    placeholder="Enter Source IP or FQDN"
                    disabled={isExecuting}
                    status={sourceIP && !validateSourceIP(sourceIP) ? 'error' : ''}
                />
            </div>
            <div className="utilities-section">
                <div className="utilities-buttons">
                    {networkTroubleshootingUtilities.map((utility) => (
                        <Button
                            key={utility.id}
                            type="primary"
                            onClick={() => handleUtilityClick(utility.id)}
                            disabled={isExecuting || !sourceIP || !validateSourceIP(sourceIP)}
                            loading={isExecuting && currentUtility === utility.id}
                        >
                            {utility.label}
                        </Button>
                    ))}
                </div>
            </div>
            <div className="result-section">
                <Text strong className="result-label">
                    Result:
                </Text>
                <pre className="network-troubleshooting-result">
                    {result}
                    {isExecuting && !result && <span className="blinking-cursor">_</span>}
                </pre>
                {isExecuting && (
                    <Text type="secondary" className="execution-note">
                        Request may take up to 30 seconds to complete
                    </Text>
                )}
            </div>
        </Modal>
    );
};

export default NetworkTroubleshootingModal;
